import { Compass, CalendarX, ListChecks, Unlink, Boxes, Languages } from 'lucide-react';
import { motion, useInView } from 'framer-motion';
import { useRef } from 'react';
import { useReducedMotion } from '../../hooks/useReducedMotion';

export function ProblemSection() {
  const ref = useRef(null);
  const isInView = useInView(ref, { once: true, margin: "-100px" });
  const prefersReducedMotion = useReducedMotion();

  const problems = [
    { icon: Compass, title: "Direction Risk", description: "Late clarity on novelty and feasibility often forces costly pivots." },
    { icon: ListChecks, title: "Citation Chaos", description: "Manual references are tedious and one mismatch undermines credibility." },
    { icon: Unlink, title: "Evidence Gaps", description: "Weak links to prior work trigger late rewrites and extra experiments." },
    { icon: Languages, title: "Language Barrier", description: "Non-native phrasing blurs ideas and reviewers question logic first." },
    { icon: Boxes, title: "Fragmented Tools", description: "Siloed tools fracture reasoning and slow the path to submission." },
    { icon: CalendarX, title: "Outcome Delays", description: "Low value tasks delay funding, graduation, publication, slowing careers." },
  ];

  return (
    <section ref={ref} className="relative py-16 sm:py-20 overflow-hidden">
      <div className="absolute inset-0 bg-gradient-to-b from-white via-slate-50 to-white dark:from-slate-900 dark:via-slate-950 dark:to-slate-900"></div>

      <div className="relative max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
        <motion.div
          className="text-center mb-10 sm:mb-12"
          initial={prefersReducedMotion ? false : { opacity: 0, y: 30 }}
          animate={isInView ? { opacity: 1, y: 0 } : {}}
          transition={{ duration: 0.6, ease: [0.22, 1, 0.36, 1] }}
        >
          <h2 className="text-3xl sm:text-4xl md:text-5xl font-bold text-[--text-primary] mb-3">
            The Research Writing Struggle
          </h2>
          <p className="text-lg sm:text-xl text-[--text-secondary] max-w-4xl mx-auto">
            Few researchers enjoy academic writing. It drains thinking rather than sharpening it.
          </p>
        </motion.div>

        {/* 紧凑网格：小屏两列 / 中大屏三列，保持较小 gap */}
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 sm:gap-5 lg:gap-6">
          {problems.map((problem, index) => {
            const Icon = problem.icon;
            return (
              <motion.div
                key={index}
                initial={prefersReducedMotion ? false : { opacity: 0, y: 40 }}
                animate={isInView ? { opacity: 1, y: 0 } : {}}
                transition={{ duration: 0.6, delay: index * 0.1, ease: [0.22, 1, 0.36, 1] }}
                className="group relative h-full bg-white/60 dark:bg-slate-800/60 backdrop-blur-sm p-6 md:p-5 rounded-2xl shadow-lg hover:shadow-xl transition-all duration-300 border border-slate-200 dark:border-slate-700"
              >
                <div className="absolute top-0 left-0 w-full h-1 bg-gradient-to-r from-red-400 via-orange-400 to-red-400 rounded-t-2xl"></div>

                {/* 这里改为水平居中 + 文本居中 */}
                <div className="flex h-full flex-col items-center text-center">
                  <div className="p-3 bg-gradient-to-br from-red-100 to-orange-100 dark:from-red-900/30 dark:to-orange-900/30 rounded-2xl mb-3 group-hover:scale-110 transition-transform duration-300">
                    <Icon className="w-7 h-7 text-red-600 dark:text-red-400" />
                  </div>
                  <h3 className="text-xl font-bold text-[--text-primary] mb-2">{problem.title}</h3>
                  <p className="text-[--text-secondary] leading-snug">{problem.description}</p>
                </div>
              </motion.div>
            );
          })}
        </div>
      </div>
    </section>
  );
}
